import type { Tab } from "@/types/dynamic-detail";

type ResolveDetailTabRedirectArgs = {
    pathname: string;
    recordBasePath: string;
    tabs: Tab[] | undefined;
};

/**
 * Returns the path of the tab the record detail page should land on, or `null` when the
 * current path is already fine (or belongs to another route).
 *
 * `tabs` must come from `detail-data`: the tab list exposed by the OPTIONS metadata is built
 * without a record instance, so tabs hidden by `visibility_conditions` are still present in it
 * and redirecting to one of them ends up on a tab the record cannot serve. Callers pass
 * `undefined` while that list is unknown (not loaded yet, or failed to load) so that no
 * redirection is decided from a fallback list.
 */
export function resolveDetailTabRedirect({ pathname, recordBasePath, tabs }: ResolveDetailTabRedirectArgs): string | null {
    if (!tabs || tabs.length === 0) return null;

    const currentPath = pathname.replace(/\/+$/, "");
    const basePath = recordBasePath.replace(/\/+$/, "");
    if (currentPath !== basePath && !currentPath.startsWith(`${basePath}/`)) return null;

    const subPath = currentPath.slice(basePath.length).replace(/^\//, "");
    // Deeper paths are handled by their own routes, not by the tab outlet.
    if (subPath.includes("/")) return null;
    if (subPath && tabs.some(tab => tab.route_path === subPath)) return null;

    const defaultTab = tabs.find(tab => tab.default) ?? tabs[0];
    return `${basePath}/${defaultTab.route_path}`;
}

type ResolveRecordBasePathArgs = {
    pathname: string;
    objectType: string;
    objectId: string | number;
    detailBaseUrl: string | null | undefined;
};

/**
 * Path the record's tabs hang from. An object can serve its detail page outside of
 * `/<object_url>/` (`DetailView.base_url`), but when the page is opened on the generic route
 * (`/<objectType>/<id>`) the tabs must stay there: under `base_url` they would point to another
 * screen, no tab would be active and the tab outlet would stay empty.
 */
export function resolveRecordBasePath({ pathname, objectType, objectId, detailBaseUrl }: ResolveRecordBasePathArgs): string {
    const genericBasePath = `/${objectType}/${objectId}`;
    if (pathname === genericBasePath || pathname.startsWith(`${genericBasePath}/`)) return genericBasePath;
    return `${(detailBaseUrl ?? `/${objectType}/`).replace(/\/*$/, "/")}${objectId}`;
}
